import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import useAuthState from '../hooks/useAuthState';
import useAuth from '../hooks/useAuth';
import { getUserProfilePlaceholder } from '../utils/getUserProfilePlaceholder';
import ProfileImageUploadModal from './ProfileImageUploadModal';
import { useDemo } from '../context/DemoContext';
import '../styles/Profile.css';

const Profile = () => {
    const { theme, toggleTheme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const { isAuthenticated, isLoading } = useAuthState();
    const { logout, refreshSession, uploadProfileImage, getProfileImage, removeProfileImage } = useAuth();
    const { isDemoMode, exitDemoMode, demoUser } = useDemo();
    const [refreshStatus, setRefreshStatus] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [isRemovingImage, setIsRemovingImage] = useState(false);

    const handleLogout = async () => {
        if (isDemoMode) {
            exitDemoMode();
            navigate('/');
            return;
        }
        try {
            await logout();
            // Clear any local storage items that might be left from old implementation
            localStorage.removeItem('accessToken');
            localStorage.removeItem('accessTokenExpiresAt');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('refreshTokenExpiresAt');
            navigate('/posts');
        } catch (error) {
            console.error('Error during logout:', error);
            // Even if logout fails, ensure we redirect to home
            navigate('/posts');
        }
    };

    const handleRefreshToken = async () => {
        try {
            setRefreshStatus('refreshing');
            await refreshSession();
            setRefreshStatus('success');
            
            // Clear the success message after a few seconds
            setTimeout(() => {
                setRefreshStatus(null);
            }, 3000);
        } catch (error) {
            console.error('Error during token refresh:', error);
            setRefreshStatus('error');
            
            // Clear the error message after a few seconds
            setTimeout(() => {
                setRefreshStatus(null);
            }, 3000);
        }
    };

    // Load profile image on component mount
    useEffect(() => {
        const loadProfileImage = async () => {
            if (isDemoMode) return;
            try {
                if (isAuthenticated && !isLoading) {
                    const result = await getProfileImage();
                    // Backend returns { profileImageUrl: "..." } where profileImageUrl is a relative path like "/api/v1/user/profile-image/filename.jpg"
                    if (result && result.profileImageUrl) {
                        // Construct full URL by combining backend base URL with relative path
                        const { getApiBaseUrl } = await import('../utils/getApiBaseUrl');
                        const apiBaseUrl = getApiBaseUrl();
                        
                        // Only prepend base URL if the image URL is not already absolute
                        const fullImageUrl = result.profileImageUrl.startsWith('http') 
                            ? result.profileImageUrl 
                            : apiBaseUrl + result.profileImageUrl;
                        
                        setProfileImageUrl(fullImageUrl);
                    }
                    else { setProfileImageUrl(null); }
                }
            } catch (error) {
                console.error('Error loading profile image:', error);
                // Don't set error state - just continue with placeholder
            }
        };
        
        loadProfileImage();
    }, [isAuthenticated, isLoading, getProfileImage]);

    const handleUploadProfileImage = async (file) => {
        try {
            setUploadStatus('uploading');
            const result = await uploadProfileImage(file);
            
            // Backend returns { profileImageUrl: "..." } where profileImageUrl is a relative path like "/api/v1/user/profile-image/filename.jpg"
            if (result && result.profileImageUrl) {
                // Construct full URL by combining backend base URL with relative path
                const { getApiBaseUrl } = await import('../utils/getApiBaseUrl');
                const apiBaseUrl = getApiBaseUrl();
                
                // Only prepend base URL if the image URL is not already absolute
                const fullImageUrl = result.profileImageUrl.startsWith('http') 
                    ? result.profileImageUrl 
                    : apiBaseUrl + result.profileImageUrl;
                
                setProfileImageUrl(fullImageUrl);
                setUploadStatus('success');
                setIsUploadModalOpen(false);
                
                // Clear success message after a few seconds
                setTimeout(() => {
                    setUploadStatus(null);
                }, 3000);
            } else {
                console.error('Unexpected response format:', result);
                setUploadStatus('error');
            }
        } catch (error) {
            console.error('Error uploading profile image:', error);
            setUploadStatus('error');
        }
    };

    const handleRemoveProfileImage = async () => {
        try {
            setIsRemovingImage(true);
            await removeProfileImage();
            
            // Clear the profile image URL to show placeholder
            setProfileImageUrl(null);
            setIsRemovingImage(false);
            
            // Show success message
            setUploadStatus('remove-success');
            
            // Clear success message after a few seconds
            setTimeout(() => {
                setUploadStatus(null);
            }, 3000);
        } catch (error) {
            console.error('Error removing profile image:', error);
            setIsRemovingImage(false);
            setUploadStatus('error');
        }
    };

    if (isLoading) {
        return (
            <div className="profile-container">
                <h2>Profile</h2>
                <p>Loading profile...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="profile-container">
                <h2>Profile</h2>
                <div className="profile-content">
                    <div className="user-info">
                        <img src={getUserProfilePlaceholder(theme)} alt="User" className="profile-avatar" />
                        <h3>Guest Profile</h3>
                        <p>Please log in to view your profile.</p>
                    </div>
                    
                    <div className="profile-actions">
                        <button
                            className="theme-toggle-button"
                            onClick={toggleTheme}
                            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        >
                            {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                        </button>
                        <button className="login-button" onClick={() => navigate('/login')}>
                            Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isDemoMode) {
        return (
            <div className="profile-container">
                <h2>Profile</h2>
                <div className="profile-content">
                    <div className="user-info">
                        <img src={getUserProfilePlaceholder(theme)} alt="User" className="profile-avatar" />
                        <h3>@{demoUser.username}</h3>
                        <p>{demoUser.email}</p>
                    </div>
                    
                    <div className="profile-actions">
                        <button
                            className="theme-toggle-button"
                            onClick={toggleTheme}
                            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                        >
                            {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                        </button>
                        <button className="logout-button" onClick={handleLogout}>
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <h2>Profile</h2>
            <div className="profile-content">
                <div className="user-info">
                    <div className="profile-avatar-container">
                        <img 
                            src={profileImageUrl ? `${profileImageUrl}?cacheBust=${Date.now()}` : getUserProfilePlaceholder(theme)} 
                            alt="User" 
                            className="profile-avatar"
                        />
                        <div className="profile-image-actions">
                            <button 
                                className="change-profile-image-button" 
                                onClick={() => setIsUploadModalOpen(true)}
                            >
                                Change Profile Image
                            </button>
                            {profileImageUrl && (
                                <button
                                    className="remove-profile-image-button"
                                    onClick={handleRemoveProfileImage}
                                    disabled={isRemovingImage}
                                >
                                    {isRemovingImage ? 'Removing...' : 'Remove Image'}
                                </button>
                            )}
                        </div>
                    </div>
                    <h3>User Profile</h3>
                    <p>Welcome to your profile page!</p>
                </div>
                
                <div className="profile-actions">
                    <button
                        className="theme-toggle-button"
                        onClick={toggleTheme}
                        aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                    >
                        {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                    </button>
                    <button className="refresh-token-button" onClick={handleRefreshToken} disabled={refreshStatus === 'refreshing'}>
                        {refreshStatus === 'refreshing' ? 'Refreshing...' : 'Refresh Token'}
                    </button>
                    <button className="logout-button" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
                {refreshStatus && (
                    <div className={`refresh-status ${refreshStatus}`}>
                        {refreshStatus === 'success' ? 'Token refreshed successfully!' : 'Failed to refresh token'}
                    </div>
                )}
                {uploadStatus && (
                    <div className={`upload-status ${uploadStatus}`}>
                        {uploadStatus === 'success' ? 'Profile image uploaded successfully!' : 
                         uploadStatus === 'remove-success' ? 'Profile image removed successfully!' : 
                         'Failed to upload profile image'}
                    </div>
                )}
            </div>
            
            <ProfileImageUploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleUploadProfileImage}
                isLoading={uploadStatus === 'uploading'}
            />
        </div>
    );
};

export default Profile;
